import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Check, X, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const ShiftPreferences = () => {
  const [loading, setLoading] = useState(true);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [preferences, setPreferences] = useState({});
  const [existingPreference, setExistingPreference] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCurrentPreference();
  }, []);

  const loadCurrentPreference = async () => {
    try {
      const res = await axios.get('/shift-preferences/current');
      setWeekStartDate(res.data.week_start_date);
      
      if (res.data.preference) {
        setExistingPreference(res.data.preference);
        setPreferences(res.data.preference.preferences || {});
      } else {
        // Initialize with empty preferences
        const prefs = {};
        const dates = generateWeekDates(res.data.week_start_date);
        dates.forEach(date => {
          prefs[date] = { preferred: true, shift: 'any', notes: '' };
        });
        setPreferences(prefs);
      }
    } catch (error) {
      console.error('Error loading preference:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const generateWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'long' });
  };

  const getFormattedDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const togglePreferred = (date) => {
    setPreferences(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        preferred: !prev[date]?.preferred
      }
    }));
  };

  const setShift = (date, shift) => {
    setPreferences(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        shift
      }
    }));
  };

  const setNotes = (date, notes) => {
    setPreferences(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        notes
      }
    }));
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      await axios.post('/shift-preferences/save', {
        week_start_date: weekStartDate,
        preferences,
        submit
      });
      
      toast.success(submit ? 'Schichtwünsche eingereicht!' : 'Entwurf gespeichert');
      loadCurrentPreference();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const dates = weekStartDate ? generateWeekDates(weekStartDate) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isSubmitted = existingPreference?.status === 'submitted';

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-4xl font-bold text-white flex items-center gap-3">
          <Calendar className="h-10 w-10" />
          Schichtwünsche
        </h1>
        <p className="text-zinc-400 mt-2">
          Geben Sie Ihre Verfügbarkeit für nächste Woche an
        </p>
      </div>

      {/* Status Card */}
      {isSubmitted && (
        <Card className="bg-green-900/20 border-green-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-green-400" />
              <div>
                <p className="font-semibold text-white">Schichtwünsche eingereicht</p>
                <p className="text-sm text-green-400">
                  Ihre Wünsche wurden am{' '}
                  {new Date(existingPreference.submitted_at).toLocaleString('de-DE')} eingereicht
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week Info */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">
            Woche vom {getFormattedDate(dates[0])} bis {getFormattedDate(dates[6])}
          </CardTitle>
          <CardDescription>
            Markieren Sie die Tage, an denen Sie arbeiten können, und geben Sie Ihre bevorzugten Schichten an.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Days */}
      <div className="space-y-4">
        {dates.map(date => {
          const dayPref = preferences[date] || { preferred: true, shift: 'any', notes: '' };
          const isPreferred = dayPref.preferred;

          return (
            <Card
              key={date}
              className={`${
                isPreferred
                  ? 'bg-green-900/20 border-green-700'
                  : 'bg-red-900/20 border-red-700'
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">
                      {getDayName(date)}
                    </CardTitle>
                    <CardDescription>{getFormattedDate(date)}</CardDescription>
                  </div>

                  <Button
                    onClick={() => togglePreferred(date)}
                    size="lg"
                    className={
                      isPreferred
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }
                    disabled={isSubmitted}
                  >
                    {isPreferred ? (
                      <>
                        <Check className="mr-2 h-5 w-5" />
                        Verfügbar
                      </>
                    ) : (
                      <>
                        <X className="mr-2 h-5 w-5" />
                        Nicht verfügbar
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              {isPreferred && (
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-zinc-300 mb-2 block">
                      <Clock className="inline h-4 w-4 mr-1" />
                      Bevorzugte Schicht
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {['any', 'morning', 'afternoon', 'evening'].map(shift => (
                        <Button
                          key={shift}
                          onClick={() => setShift(date, shift)}
                          variant={dayPref.shift === shift ? 'default' : 'outline'}
                          disabled={isSubmitted}
                          className="w-full"
                        >
                          {shift === 'any' && 'Egal'}
                          {shift === 'morning' && 'Früh'}
                          {shift === 'afternoon' && 'Mittag'}
                          {shift === 'evening' && 'Spät'}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-zinc-300 mb-2 block">
                      Notizen (optional)
                    </label>
                    <Textarea
                      value={dayPref.notes}
                      onChange={(e) => setNotes(date, e.target.value)}
                      placeholder="z.B. Nur bis 18 Uhr..."
                      className="bg-zinc-950 border-zinc-800 text-white"
                      rows={2}
                      disabled={isSubmitted}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Action Buttons */}
      {!isSubmitted && (
        <div className="flex gap-3">
          <Button
            onClick={() => handleSave(false)}
            disabled={saving}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            Entwurf speichern
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving}
            size="lg"
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              'Speichere...'
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Schichtwünsche einreichen
              </>
            )}
          </Button>
        </div>
      )}

      {/* Info */}
      <Card className="bg-blue-900/20 border-blue-700">
        <CardContent className="p-4 text-sm text-zinc-300 space-y-1">
          <p>💡 Ihre Eingaben werden berücksichtigt, aber nicht garantiert</p>
          <p>📅 Bitte füllen Sie die Wunschliste bis Sonntag 23:59 Uhr aus</p>
          <p>✉️ Nach der Einreichung können Sie keine Änderungen mehr vornehmen</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftPreferences;
